import type {ReactElement, ReactNode} from 'react';
import React from 'react';
import {AiChatMobileNav} from '@/ai-chat/components/ai-chat-mobile-nav/ai-chat-mobile-nav';
import {AiChatBody} from '@/ai-chat/components/ai-chat-body/ai-chat-body';
import {AiChatProvider} from '@/ai-chat/providers/ai-chat-provider';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import styles from './ai-chat.module.scss';
import {AiChatHistoryDrawer} from '@/ai-chat/components/ai-chat-history-drawer/ai-chat-history-drawer';
import type {ChatOnLoadParams} from '@Components/ai-chat-window/ai-chat-window.types';
import {AiChatLoadingStates} from '@/ai-chat/components/ai-chat-loading-states/ai-chat-loading-states';
import {AiChatWindowInspirations, aiChatWindowInspirationsData} from '@/ai-chat/components/ai-chat-window-inspirations';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiChatFooter} from '@/ai-chat/components/ai-chat-footer/ai-chat-footer';
import {AiChatNav} from '@/ai-chat/components/ai-chat-nav';
import {AI_CHAT_HOME_SCROLL_ID} from '@/ai-chat/ai-chat.types';
import {getAiChatPromptBoxId} from '@Libraries/ai-chat.library';
import {AiChatDragDropOverlay} from '@/ai-chat/components/ai-chat-drag-drop-overlay';
import {AiChatBanner} from '@/ai-chat/components/ai-chat-banner';
import type {AiChatBannerData} from '@/ai-chat/components/ai-chat-banner/ai-chat-banner.types';
import {AiChatSidebar} from '@AiChat/components/ai-chat-sidebar/ai-chat-sidebar';

export interface AiChatProps {
  chatOnLoadParams: ChatOnLoadParams;
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
  isAiChatCreateAVideoFeatureEnabled: boolean;
  isTemplateRedesignFeatureEnabled: boolean;
  banner?: AiChatBannerData;
}

function AiChatContent({...props}: AiChatProps): ReactElement {
  const {state} = useAiChat();
  const isMobile = state.isSmall;
  const promptBoxId = getAiChatPromptBoxId(state.currentChatId);
  const {actions: promptBoxActions} = useAiPromptBox(promptBoxId);
  const inspirationsData = aiChatWindowInspirationsData(promptBoxActions.update);
  const hasMessages = !!state.currentChatId;
  const isNewUserEmptyState = !hasMessages && !state.isReturningUser;

  const isMobileReturningUser = isMobile && state.isReturningUser && !hasMessages;

  const getFullScreenClassName = (): string => {
    const classNames = [styles.fullScreen];

    if (isNewUserEmptyState && !(isMobile && state.isSidebarExpanded)) {
      classNames.push(styles.scrollable);
    }

    if (window.PMW.isFirefox()) {
      classNames.push(styles.firefoxFix);
    }

    return classNames.join(' ');
  };

  const getPageContentClassName = (): string => {
    if (isNewUserEmptyState) {
      return `${styles.pageContent} ${styles.scrollable}`;
    }
    return styles.pageContent;
  };

  const renderHistoryDrawer = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }
    return <AiChatHistoryDrawer />;
  };

  const renderMobileHistoryDrawer = (): ReactElement | null => {
    if (!isMobile) {
      return null;
    }
    return <AiChatHistoryDrawer />;
  };

  const renderNavigationBar = (): ReactElement => {
    if (isMobile) {
      return <AiChatMobileNav />;
    }
    return <AiChatNav />;
  };

  const renderEmptyStateContent = (): ReactElement | null => {
    if (hasMessages || state.isQuickTipsView) {
      return null;
    }

    if (state.isReturningUser) {
      return <div className={`${styles.returningUserBackground} ${isMobile ? styles.returningUserBackgroundMobile : ''}`} />;
    }

    return <AiChatWindowInspirations inspirations={inspirationsData} />;
  };

  const renderAiChatBannerIfNeeded = (): ReactElement | null => {
    if (!props.banner) {
      return null;
    }
    return <AiChatBanner banner={props.banner} />;
  };

  const renderDesktopDragDropOverlay = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }
    return <AiChatDragDropOverlay promptBoxId={getAiChatPromptBoxId(state.currentChatId)} />;
  };

  const renderSideBar = (): ReactNode => {
    if (state.isSmall) {
      return null;
    }

    return <AiChatSidebar />;
  };

  return (
    <div id={AI_CHAT_HOME_SCROLL_ID} className={getFullScreenClassName()}>
      {renderAiChatBannerIfNeeded()}
      {renderHistoryDrawer()}
      <div className={getPageContentClassName()}>
        {renderNavigationBar()}
        {renderSideBar()}
        <div className={`_position-relative ${isMobileReturningUser ? styles.mobileReturningUserContent : '_full-height'}`}>
          <AiChatLoadingStates />
          <AiChatBody {...props} />
          {renderEmptyStateContent()}
          <AiChatFooter />
        </div>
      </div>
      {renderMobileHistoryDrawer()}
      {renderDesktopDragDropOverlay()}
    </div>
  );
}

export function AiChat({...props}: AiChatProps): ReactElement {
  return (
    <AiChatProvider
      credits={props.credits}
      isPaygUserWithAiChatAccess={props.isPaygUserWithAiChatAccess}
      isReturningUser={props.isReturningUser}
      isSuperUser={props.isSuperUser}
      isAiChatCreateAVideoFeatureEnabled={props.isAiChatCreateAVideoFeatureEnabled}
      isTemplateRedesignFeatureEnabled={props.isTemplateRedesignFeatureEnabled}
    >
      <AiChatContent {...props} />
    </AiChatProvider>
  );
}
